import { makeStyles } from '@material-ui/styles';
import { useFeatureToggle } from '@bizzabo/feature-toggle-client';
import { useContext } from 'react';
import cn from 'clsx';
import Lottie from 'react-lottie';
import { useAtomValue } from 'jotai';

import { Theme, Text } from '@bizzabo/design-system';

import {
    AppToolId,
    IAppNotification,
    IAppToolbarItem,
    IAppToolsContext,
} from '../../types/appTools';
import AppToolsContext from '../../context/AppToolsContext';
import useLottieAnimation from '../../decorators/useLottieAnimation';
import useIsMobile from '../../../hooks/useIsMobile';
import { IsExpandedAppTool, SelectedAppToolAtom } from '../../atoms/appToolsAtom';
import AppToolbarItemAwareness from './AppToolbarItemAwareness';

const emptyUseNotification: IAppNotification = {
    hasNewNotification: false,
    resetNotification: () => {},
};

const AppToolbarItem: React.FC<IAppToolbarItem> = ({
    id,
    icon,
    animatedIcon,
    iconUrl,
    title,
    isSelected,
    awareness,
    onAppSelected,
    useNotification = () => emptyUseNotification,
    trackIconClick = () => {},
}) => {
    const classes = useStyles();
    const { eventUniqueName } = useContext<IAppToolsContext>(AppToolsContext);

    const isExpanded = useAtomValue(IsExpandedAppTool);
    const selectedApp = useAtomValue(SelectedAppToolAtom);
    const shouldHideMessagesIcon = useFeatureToggle('venue-hide-messages-icon');

    const { hasNewNotification, resetNotification, shouldResetNotificationOnSelect } =
        useNotification({ eventUniqueName });

    const isMobile = useIsMobile();

    const shouldUseAnimation = !!animatedIcon && !isMobile;
    const { lottieRef, play, stop } = useLottieAnimation(shouldUseAnimation);

    const defaultOptions = {
        loop: true,
        autoplay: false,
        animationData: animatedIcon,
    };

    const onAppSelectedHandle = (app: AppToolId) => {
        onAppSelected(app);
    };

    const trackIconClickHandler = (app: AppToolId, shouldShowNotification: boolean) => {
        const isActionOpenApp = !isExpanded || app != selectedApp;
        if (isActionOpenApp) {
            trackIconClick(app, shouldShowNotification);
        }
    };
    const shouldHideMessageWithToggle = shouldHideMessagesIcon && title === 'Messages';
    return !shouldHideMessageWithToggle ? (
        <AppToolbarItemAwareness
            awareness={awareness}
            toolId={id}
            onAppSelectedHandle={onAppSelectedHandle}
        >
            {({ isDotAwarenessOn, anchorRef, awarenessHandle }) => {
                const shouldShowNotification =
                    (hasNewNotification || isDotAwarenessOn) && !isSelected;

                return (
                    <div
                        ref={anchorRef}
                        className={cn(classes.container, {
                            [classes.selected]: isSelected,
                        })}
                        onClick={() => {
                            onAppSelectedHandle(id);
                            trackIconClickHandler(id, shouldShowNotification);
                            awarenessHandle();
                            if (shouldResetNotificationOnSelect) {
                                resetNotification();
                            }
                        }}
                        onMouseEnter={play}
                        onMouseLeave={stop}
                    >
                        <div className={classes.icon}>
                            {shouldShowNotification && <div className={classes.notification} />}
                            {shouldUseAnimation && (
                                <Lottie
                                    ref={lottieRef}
                                    options={defaultOptions}
                                    height={30}
                                    width={30}
                                />
                            )}
                            {!shouldUseAnimation && icon}
                            {!shouldUseAnimation && !icon && !!iconUrl && (
                                <img
                                    className={cn({
                                        [classes.rewardsImage]: title === 'Rewards',
                                        [classes.regularImage]: title !== 'Rewards',
                                    })}
                                    src={iconUrl}
                                    alt={title}
                                />
                            )}
                        </div>
                        <Text variant="t90" className={classes.text}>
                            {title}
                        </Text>
                    </div>
                );
            }}
        </AppToolbarItemAwareness>
    ) : null;
};

const useStyles = makeStyles<Theme>(
    theme => ({
        container: {
            width: 70,
            minHeight: 58,
            background: 'white',
            borderRadius: 12,
            padding: '8px 5px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: {
                minWidth: 70,
                height: 60,
                minHeight: 50,
                width: 'auto',
                padding: '6px 8px 10px',
                margin: '8px 0',
                '-webkit-tap-highlight-color': 'transparent',
            },
            ['@media (hover: hover)']: {
                '&:hover': {
                    background: `linear-gradient(0deg,  #F4F4F4,  #F4F4F4), #FFFFFF`,
                },
            },
        },
        icon: {
            position: 'relative',
            marginBottom: 8,
            [theme.breakpoints.down('sm')]: {
                marginBottom: 2,
            },
        },
        rewardsImage: {
            height: 26,
            width: 26,
        },
        regularImage: {
            height: 20,
            width: 18,
        },
        notification: {
            background: theme.palette.notification,
            width: 12,
            height: 12,
            borderRadius: '50%',
            border: '1px solid #FFFFFF',

            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
        },
        selected: {
            background: `linear-gradient(0deg,  #F4F4F4,  #F4F4F4), #FFFFFF`,
        },
        text: {
            width: 60,
            textAlign: 'center',
            userSelect: 'none',
            [theme.breakpoints.down('sm')]: {
                width: 'auto',
            },
        },
    }),
    {
        classNamePrefix: 'AppToolbarItem',
    }
);

export default AppToolbarItem;
